import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Inbox, FileText, Settings2, ExternalLink } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const TABS = [
  { key: "all", label: "All", icon: Inbox },
  { key: "unread", label: "Unread", icon: Bell },
  { key: "requests", label: "Requests", icon: FileText },
  { key: "system", label: "System", icon: Settings2 },
];

const kindLabel = (k) => ({
  goods_acceptance: "Goods Acceptance", material_request: "Material Request",
  accepted: "Accepted", issued: "Issued", info: "System",
}[k] || "Notification");

export default function NotificationCenter() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    try { setItems((await api.get("/notifications")).data || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const markOne = async (id) => { try { await api.post(`/notifications/${id}/read`); setItems((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n))); } catch (e) { console.error("Failed to mark notification read", e); } };
  const markAll = async () => { try { await api.post("/notifications/read-all"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const unreadCount = items.filter((n) => !n.read).length;
  const filtered = useMemo(() => {
    if (tab === "unread") return items.filter((n) => !n.read);
    if (tab === "requests") return items.filter((n) => n.request_id);
    if (tab === "system") return items.filter((n) => !n.request_id);
    return items;
  }, [items, tab]);

  return (
    <div data-testid="notification-center">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div><h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head flex items-center gap-2"><Bell className="w-6 h-6" /> Notifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">{unreadCount} unread of {items.length}</p></div>
        {items.length > 0 && <Button data-testid="nc-mark-all" onClick={markAll} disabled={unreadCount === 0} variant="outline" className="rounded-sm gap-1.5"><CheckCheck className="w-4 h-4" /> Mark all as read</Button>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="nc-tabs">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`nc-tab-${t.key}`} className="gap-1.5"><t.icon className="w-3.5 h-3.5" /> {t.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100">
        {filtered.length === 0 ? (
          <div className="px-4 py-14 text-center text-slate-400" data-testid="nc-empty">No notifications here.</div>
        ) : filtered.map((n) => (
          <div key={n.id} data-testid={`nc-item-${n.id}`} onClick={() => !n.read && markOne(n.id)} className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/40"}`}>
            <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-slate-200" : "bg-blue-500"}`} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="rounded-sm text-[10px] border-slate-200 text-slate-600">{kindLabel(n.kind)}</Badge>
                {n.request_id && <Badge variant="outline" className="rounded-sm text-[10px] border-amber-200 text-amber-700">Request</Badge>}
                {!n.read && <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Unread</span>}
              </div>
              <div className="text-sm text-slate-800 mt-1">{n.message}</div>
              <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                <span>{new Date(n.created_at).toLocaleString()}</span>
                {n.sender_name && <span>· From: {n.sender_name}{n.sender_role ? ` (${n.sender_role})` : ""}</span>}
                {n.receiver && <span>· To: {n.receiver}</span>}
                {n.status && <span>· Status: {n.status}</span>}
                {n.request_no && <span>· {n.request_no}</span>}
              </div>
            </div>
            {n.request_id
              ? <Button data-testid={`nc-open-${n.id}`} size="sm" variant="outline" className="text-xs shrink-0 gap-1" onClick={(e) => { e.stopPropagation(); if (!n.read) markOne(n.id); navigate(`/requests?open=${n.request_id}`); }}><ExternalLink className="w-3.5 h-3.5" /> Open Request</Button>
              : (!n.read && <Button size="sm" variant="ghost" className="text-xs text-slate-500 shrink-0" onClick={(e) => { e.stopPropagation(); markOne(n.id); }}>Mark read</Button>)}
          </div>
        ))}
      </div>
    </div>
  );
}
